import {useScoutingVisions} from "@/utils/scoutingVisionData";
import {useScoutingReports} from "@/utils/scoutingReportData";
import ScoutingReportsStatus from "@/components/ScoutingReportsStatus";
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";
import moment from "moment";

import {buildPlayerBody} from "@/utils/functions";
import Page from "@/components/Page";
import Loading from "@/components/Loading";
import VLFSelectNew from "@/components/Input/SelectNew";
import Breadcrumbs from "@/components/Breadcrumbs";
import ChoiceChips from "@/components/ChoiceChips";
import MainActions from "@/components/MainActions";
import VLFTextField from "@/components/Input/TextField";
import ScoutPlayerTile from "@/components/ScoutPlayerTile";
import VLFDatePicker from "@/components/Input/DatePicker";

import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import AddIcon from "@mui/icons-material/Add";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PrintIcon from "@mui/icons-material/Print";

import {PlayerAPI} from "@/api/player";
import Alert from "@mui/material/Alert";
import {
	PIPELINE_STAGES as STAGES,
	isMonitored,
	useScoutingPipeline,
	putPipelineEntry,
	removePipelineEntry,
} from "@/utils/scoutingPipeline";
import {ATTITUDES, DEPARTMENTS, playerDepartment} from "@/utils/scoutingData";
import {visionSummaries} from "@/utils/scoutingVisions";
import {getVerdict, reportSummaries} from "@/utils/scoutingReports";
import {AREA_OPTIONS, ERROR_OPERATION, REQUIRED_FIELD} from "@/utils/constants";

import "./index.scss";

const STAGE_IDS = STAGES.map((s) => s.id);

const SORT_OPTIONS = [
	{value: "recent", label: "Ultimi aggiunti"},
	{value: "viewed", label: "Più visti"},
	{value: "young", label: "Più giovani"},
];

const DEPARTMENT_SORTS = {
	viewed: (a, b) => (b.visions?.count || 0) - (a.visions?.count || 0),
	recent: (a, b) => new Date(b.entry.addedAt) - new Date(a.entry.addedAt),
	young: (a, b) =>
		(b.player?.dateOfBirth || "").localeCompare(a.player?.dateOfBirth || ""),
};

// ── Helpers ────────────────────────────────────────────────────────────────

function playerAge(p) {
	return p?.dateOfBirth ? moment().diff(moment(p.dateOfBirth), "years") : null;
}

function playerRole(p) {
	return p?.goalkeeper ? "P" : p?.primaryRole?.slice(0, 3) || "—";
}

// ── Player Card ────────────────────────────────────────────────────────────

function PipelineCard({
	canWrite,
	entry,
	player,
	rating,
	visions,
	reports,
	stageIndex,
	onMove,
	onRemove,
	onNoteChange,
	onDragStart,
	onDragEnd,
	isDragOver,
	onDragOver,
	onDrop,
}) {
	const navigate = useNavigate();
	const [editingNote, setEditingNote] = useState(false);
	const [noteValue, setNoteValue] = useState(entry.note || "");
	useEffect(() => {
		if (!editingNote) setNoteValue(entry.note || "");
	}, [entry.note, editingNote]);

	const handleNoteBlur = () => {
		setEditingNote(false);
		if (noteValue !== entry.note) {
			onNoteChange(noteValue);
		}
	};

	const age = playerAge(player);
	const role = playerRole(player);
	const name = player
		? `${player.firstName || ""} ${player.lastName || ""}`.trim()
		: "…";
	const category = player?.playerCategory?.name || player?.playerCategoryName;

	return (
		<div
			className={`pipe-card${isDragOver ? " pipe-card--drag-over" : ""}`}
			draggable={canWrite && !!onDragStart}
			onDragStart={() => onDragStart?.(entry.playerId)}
			onDragEnd={onDragEnd}
			onDragOver={(e) => {
				if (!onDragOver) return;
				e.preventDefault();
				onDragOver(entry.playerId);
			}}
			onDrop={(e) => {
				if (!onDrop) return;
				e.stopPropagation();
				onDrop(entry.playerId);
			}}
		>
			{/* Top row */}
			<div className="pipe-card__top">
				<span className="pipe-card__role">{role}</span>
				<span
					className="pipe-card__name"
					onClick={() =>
						player?.id && navigate("/scouting/players/" + player.id)
					}
				>
					{name}
				</span>
				{age !== null && <span className="pipe-card__age">{age}</span>}
			</div>

			{/* Meta row */}
			<div className="pipe-card__meta">
				{category && <span className="pipe-card__category">{category}</span>}
				{rating !== null && rating !== undefined && (
					<span className="pipe-card__rating">{rating.toFixed(1)} ★</span>
				)}
				{getVerdict(reports?.lastVerdict) && (
					<span
						className="pipe-card__verdict"
						style={{color: getVerdict(reports.lastVerdict).color}}
						title={`${reports.reportsCount} ${reports.reportsCount === 1 ? "relazione" : "relazioni"}`}
					>
						{getVerdict(reports.lastVerdict).label}
					</span>
				)}
				{visions?.count > 0 && (
					<span className="pipe-card__visions">
						{visions.count} {visions.count === 1 ? "visione" : "visioni"}
					</span>
				)}
				{ATTITUDES.filter((a) => visions?.attitudes?.[a.id]).map((a) => (
					<span
						key={a.id}
						className="pipe-card__attitude"
						style={{"--attitude-color": a.color}}
						title={`${a.label}: ${visions.attitudes[a.id]} su ${visions.count}`}
					>
						{a.id}
					</span>
				))}
				<span
					className="pipe-card__added"
					title={`Aggiunto il ${moment(entry.addedAt).format("DD/MM/YYYY")}`}
				>
					{moment(entry.addedAt).fromNow()}
				</span>
			</div>

			{/* Note */}
			{editingNote ? (
				<div className="pipe-card__note-input" onBlur={handleNoteBlur}>
					<VLFTextField
						value={noteValue}
						onChange={(e) => setNoteValue(e.target.value)}
						multiline
						minRows={2}
						maxRows={6}
						size="small"
						placeholder="Aggiungi nota..."
						inputProps={{"aria-label": "Nota", autoFocus: true}}
					/>
				</div>
			) : (
				<div
					className={`pipe-card__note${entry.note ? "" : " pipe-card__note--empty"}`}
					onClick={() => canWrite && setEditingNote(true)}
				>
					{entry.note || "Aggiungi nota…"}
				</div>
			)}

			{/* Actions */}
			<div className="pipe-card__actions">
				<div className="pipe-card__moves">
					{stageIndex > 0 && (
						<button
							className="pipe-card__move pipe-card__move--back"
							disabled={!canWrite}
							onClick={() => onMove(STAGE_IDS[stageIndex - 1])}
							title={`Sposta a: ${STAGES[stageIndex - 1].label}`}
							aria-label={`Sposta ${name} a ${STAGES[stageIndex - 1].label}`}
						>
							‹
						</button>
					)}
					{stageIndex < STAGES.length - 1 && (
						<button
							className="pipe-card__move pipe-card__move--fwd"
							disabled={!canWrite}
							onClick={() => onMove(STAGE_IDS[stageIndex + 1])}
							title={`Sposta a: ${STAGES[stageIndex + 1].label}`}
							aria-label={`Sposta ${name} a ${STAGES[stageIndex + 1].label}`}
						>
							›
						</button>
					)}
				</div>
				<div className="pipe-card__right-actions">
					{player?.id && (
						<button
							className="pipe-card__link"
							onClick={() => navigate("/scouting/players/" + player.id)}
							title="Apri profilo"
						>
							<OpenInNewIcon sx={{fontSize: 13}} />
						</button>
					)}
					<button
						className="pipe-card__remove"
						disabled={!canWrite}
						onClick={() => onRemove(entry.playerId)}
						title="Togli dagli obiettivi"
					>
						×
					</button>
				</div>
			</div>
		</div>
	);
}

// ── Column ─────────────────────────────────────────────────────────────────

function PipelineColumn({
	canWrite,
	stage,
	stageIndex,
	entries,
	players,
	ratings,
	onAdd,
	onMove,
	onRemove,
	onNoteChange,
	dragRef,
	dragOverTarget,
	setDragOverTarget,
	handleDrop,
	handleDragEnd,
}) {
	const isEmpty = entries.length === 0;

	return (
		<div
			className="pipe-column"
			style={{"--stage-color": stage.color, "--stage-border": stage.border}}
			onDragOver={(e) => {
				e.preventDefault();
				if (dragRef.current && dragRef.current.targetId !== stage.id) {
					setDragOverTarget({type: "column", id: stage.id});
				}
			}}
			onDrop={(e) => {
				e.preventDefault();
				handleDrop(stage.id, null);
			}}
		>
			{/* Column header */}
			<div
				className="pipe-column__header"
				style={{borderBottomColor: stage.border}}
			>
				<div className="pipe-column__title-row">
					<span className="pipe-column__dot" style={{background: stage.dot}} />
					<span className="pipe-column__label" style={{color: stage.color}}>
						{stage.label}
					</span>
					<span className="pipe-column__count">{entries.length}</span>
				</div>
				<button
					className="pipe-column__add"
					disabled={!canWrite}
					onClick={() => onAdd(stage.id)}
					title={`Aggiungi a ${stage.label}`}
				>
					+
				</button>
			</div>

			{/* Cards */}
			<div
				className={`pipe-column__cards${
					dragOverTarget?.type === "column" && dragOverTarget?.id === stage.id
						? " pipe-column__cards--drag-over"
						: ""
				}`}
			>
				{isEmpty && <div className="pipe-column__empty">Nessun giocatore</div>}
				{entries.map((entry) => (
					<PipelineCard
						canWrite={canWrite}
						key={entry.playerId}
						entry={entry}
						player={players[entry.playerId]}
						rating={ratings[entry.playerId]}
						stageIndex={stageIndex}
						onMove={(targetStatus) => onMove(entry.playerId, targetStatus)}
						onRemove={onRemove}
						onNoteChange={(note) => onNoteChange(entry.playerId, note)}
						onDragStart={(id) => {
							dragRef.current = {playerId: id, sourceStage: stage.id};
						}}
						onDragEnd={handleDragEnd}
						isDragOver={
							dragOverTarget?.type === "card" &&
							dragOverTarget?.id === entry.playerId
						}
						onDragOver={(id) => setDragOverTarget({type: "card", id})}
						onDrop={(beforeId) => handleDrop(stage.id, beforeId)}
					/>
				))}
			</div>
		</div>
	);
}

// ── Da monitorare per reparto ──────────────────────────────────────────────

function DepartmentBoard({
	canWrite,
	pipeline,
	players,
	ratings,
	visions,
	reports,
	sort,
	onAdd,
	onMove,
	onRemove,
	onNoteChange,
}) {
	const rows = Object.values(pipeline)
		.filter(isMonitored)
		.map((entry) => ({
			entry,
			player: players[entry.playerId],
			visions: visions[entry.playerId],
			reports: reports[entry.playerId],
		}))
		.sort(DEPARTMENT_SORTS[sort]);
	const columns = [
		...DEPARTMENTS,
		{id: null, label: "Ruolo non indicato"},
	].filter((d) => d.id || rows.some((r) => !playerDepartment(r.player)));

	return (
		<div className="pipe-board">
			{columns.map((department) => {
				const items = rows.filter(
					(r) => playerDepartment(r.player) === department.id,
				);
				return (
					<div key={department.id || "none"} className="pipe-column">
						<div className="pipe-column__header">
							<div className="pipe-column__title-row">
								<span className="pipe-column__label">{department.label}</span>
								<span className="pipe-column__count">{items.length}</span>
							</div>
							<button
								className="pipe-column__add"
								disabled={!canWrite}
								onClick={() => onAdd("watching")}
								title="Aggiungi a Monitorato"
							>
								+
							</button>
						</div>
						<div className="pipe-column__cards">
							{!items.length && (
								<div className="pipe-column__empty">Nessun giocatore</div>
							)}
							{items.map(({entry, player, visions, reports}) => (
								<PipelineCard
									canWrite={canWrite}
									key={entry.playerId}
									entry={entry}
									player={player}
									rating={ratings[entry.playerId]}
									visions={visions}
									reports={reports}
									stageIndex={0}
									onMove={(targetStatus) =>
										onMove(entry.playerId, targetStatus)
									}
									onRemove={onRemove}
									onNoteChange={(note) => onNoteChange(entry.playerId, note)}
								/>
							))}
						</div>
					</div>
				);
			})}
		</div>
	);
}

// ── Add Player Modal ───────────────────────────────────────────────────────

function AddPlayerModal({
	open,
	stageName,
	onClose,
	onAdd,
	alreadyInPipeline,
	handleNotification,
}) {
	const seasonId = useSelector((state) => state?.season?.id);

	// Tab state
	const [tab, setTab] = useState("search"); // "search" | "new"

	// Search tab
	const [query, setQuery] = useState("");
	const [results, setResults] = useState([]);
	const [searching, setSearching] = useState(false);
	const debounceRef = useRef(null);

	// Create tab
	const [newFields, setNewFields] = useState({});
	const [newErrors, setNewErrors] = useState([]);
	const [creating, setCreating] = useState(false);

	useEffect(() => {
		if (!open) {
			setQuery("");
			setResults([]);
			setTab("search");
			setNewFields({});
			setNewErrors([]);
		}
	}, [open]);

	// Search debounce
	useEffect(() => {
		if (!query.trim()) {
			setResults([]);
			return;
		}
		clearTimeout(debounceRef.current);
		debounceRef.current = setTimeout(async () => {
			setSearching(true);
			try {
				const res = await PlayerAPI.paginated({
					page: 1,
					pageSize: 20,
					prospect: true,
					search: query,
					seasonId,
				});
				setResults(res.data || []);
			} catch {
				setResults([]);
			} finally {
				setSearching(false);
			}
		}, 280);
	}, [query]);

	// Create new player
	const handleCreate = async () => {
		const errors = [];
		if (!newFields.lastName?.trim()) errors.push("lastName");
		if (!newFields.primaryRole) errors.push("primaryRole");
		if (errors.length) {
			setNewErrors(errors);
			return;
		}

		setCreating(true);
		try {
			const isGoalkeeper = newFields.primaryRole?.value === "Goalkeeper";
			const created = await PlayerAPI.create(
				buildPlayerBody({
					firstName: newFields.firstName?.trim(),
					lastName: newFields.lastName?.trim(),
					dateOfBirth: newFields.dateOfBirth,
					goalkeeper: isGoalkeeper,
					primaryRole: isGoalkeeper ? null : newFields.primaryRole?.label,
				}),
			);
			onAdd(created.id, created);
		} catch {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setCreating(false);
		}
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
			<DialogTitle
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					pb: 1,
					fontSize: 15,
					fontWeight: 700,
				}}
			>
				<span>
					Aggiungi a: <b>{stageName}</b>
				</span>
				<IconButton size="small" onClick={onClose}>
					<CloseIcon fontSize="small" />
				</IconButton>
			</DialogTitle>
			<DialogContent sx={{pt: "8px !important"}}>
				{/* Tab toggle */}
				<ButtonGroup
					className="pipe-modal-tabs"
					disableElevation
					size="small"
					aria-label="Modalità"
				>
					<Button
						variant={tab === "search" ? "contained" : "outlined"}
						onClick={() => setTab("search")}
					>
						Cerca nel database
					</Button>
					<Button
						variant={tab === "new" ? "contained" : "outlined"}
						onClick={() => setTab("new")}
					>
						Crea nuovo
					</Button>
				</ButtonGroup>

				{/* Search tab */}
				{tab === "search" && (
					<>
						<VLFTextField
							placeholder="Cerca giocatore per nome…"
							value={query}
							onChange={(e) => setQuery(e.target.value)}
							size="small"
							inputProps={{"aria-label": "Cerca giocatore", autoFocus: true}}
						/>
						{searching && (
							<div className="pipe-search-hint">Ricerca in corso…</div>
						)}
						{!searching && query && results.length === 0 && (
							<div className="pipe-search-hint">
								Nessun risultato —{" "}
								<button
									className="pipe-search-hint__create"
									onClick={() => setTab("new")}
								>
									crea nuovo giocatore
								</button>
							</div>
						)}
						{!query && (
							<div className="pipe-search-hint">
								Digita il nome del giocatore da cercare nel database scouting
							</div>
						)}
						<div className="pipe-search-results">
							{results.map((p) => {
								const inPipeline = alreadyInPipeline.has(p.id);
								return (
									<ScoutPlayerTile
										key={p.id}
										player={p}
										onClick={inPipeline ? undefined : () => onAdd(p.id, p)}
										action={
											inPipeline && (
												<span className="pipe-search-row__already">
													Già negli obiettivi
												</span>
											)
										}
									/>
								);
							})}
						</div>
					</>
				)}

				{/* Create new tab */}
				{tab === "new" && (
					<div className="pipe-create-form">
						<div className="pipe-create-form__row">
							<div>
								<VLFTextField
									label="Nome"
									placeholder="Nome"
									size="small"
									value={newFields.firstName || ""}
									onChange={(e) =>
										setNewFields({...newFields, firstName: e.target.value})
									}
								/>
							</div>
							<div>
								<VLFTextField
									label="Cognome"
									required
									placeholder="Cognome"
									size="small"
									value={newFields.lastName || ""}
									error={newErrors.includes("lastName")}
									helperText={
										newErrors.includes("lastName") ? REQUIRED_FIELD : ""
									}
									onChange={(e) => {
										setNewErrors(newErrors.filter((e) => e !== "lastName"));
										setNewFields({...newFields, lastName: e.target.value});
									}}
								/>
							</div>
						</div>
						<div className="pipe-create-form__row">
							<div>
								<VLFSelectNew
									label="Ruolo"
									required
									options={AREA_OPTIONS}
									sorted={false}
									value={newFields.primaryRole || null}
									onChange={(v) => {
										setNewErrors(newErrors.filter((e) => e !== "primaryRole"));
										setNewFields({...newFields, primaryRole: v});
									}}
									placeholder="Seleziona ruolo"
									error={newErrors.includes("primaryRole")}
									helperText={
										newErrors.includes("primaryRole") ? REQUIRED_FIELD : ""
									}
								/>
							</div>
							<div>
								<VLFDatePicker
									label="Data di nascita"
									value={newFields.dateOfBirth || null}
									onChange={(v) => setNewFields({...newFields, dateOfBirth: v})}
								/>
							</div>
						</div>
						<div className="pipe-create-form__actions">
							<MainActions
								actions={[
									{
										icon: <AddIcon />,
										label: creating ? "Creazione…" : "Crea e aggiungi",
										title: "Crea e aggiungi",
										disabled: creating,
										onClick: handleCreate,
									},
								]}
							/>
						</div>
					</div>
				)}
			</DialogContent>
		</Dialog>
	);
}

// ── Main Page ──────────────────────────────────────────────────────────────

export default function ScoutingPipelinePage(props) {
	const {handleNotification} = props;

	const {
		pipeline,
		loading: loadingPlayers,
		error: pipelineError,
		canWrite,
		refresh,
	} = useScoutingPipeline();
	const players = Object.fromEntries(
		Object.values(pipeline).map((entry) => [entry.playerId, entry.player]),
	);
	const ratings = {};
	const visionState = useScoutingVisions();
	const visions = useMemo(
		() => visionSummaries(visionState.visions),
		[visionState.visions],
	);
	const reportData = useScoutingReports();
	const reports = useMemo(
		() => reportSummaries(reportData.reports),
		[reportData.reports],
	);

	// Add modal
	const [addModal, setAddModal] = useState(null); // null | stageId
	const [view, setView] = useState("stages");
	const [departmentSort, setDepartmentSort] = useState("recent");

	// Drag & drop
	const dragRef = useRef(null);
	const [dragOverTarget, setDragOverTarget] = useState(null);

	const run = async (operation) => {
		try {
			await operation();
			return true;
		} catch (error) {
			handleNotification(
				error.response?.data?.error ||
					error.message ||
					"Salvataggio non riuscito",
				"error",
			);
			return false;
		}
	};
	const handleAdd = async (playerId) => {
		if (
			await run(() => putPipelineEntry(playerId, {status: addModal, note: ""}))
		)
			setAddModal(null);
	};
	const handleMove = (id, status) =>
		run(() => putPipelineEntry(id, {status, note: pipeline[id]?.note || ""}));
	const handleRemove = (id) => run(() => removePipelineEntry(id));
	const handleNoteChange = (id, note) =>
		run(() => putPipelineEntry(id, {status: pipeline[id].status, note}));

	// DnD handlers
	const handleDragEnd = useCallback(() => {
		dragRef.current = null;
		setDragOverTarget(null);
	}, []);

	const handleDrop = (targetStageId) => {
		if (!canWrite || !dragRef.current) return;
		const {playerId} = dragRef.current;
		if (playerId) handleMove(playerId, targetStageId);
		dragRef.current = null;
		setDragOverTarget(null);
	};

	// ── Derived data ──────────────────────────────────────────────────────

	const totalPlayers = Object.keys(pipeline).length;
	const alreadyInPipeline = new Set(Object.keys(pipeline));

	const getEntriesForStage = (stageId) =>
		Object.values(pipeline)
			.filter((e) => e.status === stageId)
			.sort((a, b) => new Date(b.addedAt) - new Date(a.addedAt));

	const addModalStage = STAGES.find((s) => s.id === addModal);

	// ── Render ────────────────────────────────────────────────────────────

	if (pipelineError)
		return (
			<Page>
				<ScoutingReportsStatus data={reportData} />
				{visionState.readError && (
					<Alert severity="warning">
						Visioni non disponibili.{" "}
						<Button onClick={() => visionState.refresh().catch(() => {})}>
							Riprova
						</Button>
					</Alert>
				)}
				<Alert severity="error">
					Pipeline non disponibile o accesso non consentito.{" "}
					<Button onClick={() => refresh().catch(() => {})}>Riprova</Button>
				</Alert>
			</Page>
		);
	return (
		<Page freeHeight>
			<ScoutingReportsStatus data={reportData} />
			{visionState.readError && (
				<Alert severity="warning">
					Visioni non disponibili.{" "}
					<Button onClick={() => visionState.refresh().catch(() => {})}>
						Riprova
					</Button>
				</Alert>
			)}
			<Breadcrumbs
				current="Obiettivi di mercato"
				actions={
					<MainActions
						actions={[
							{
								icon: <PrintIcon />,
								title: "Esporta PDF",
								onClick: () => window.print(),
							},
						]}
					/>
				}
			/>
			<Loading visible={loadingPlayers} />
			{pipelineError && (
				<Alert severity="error">
					Pipeline non disponibile o accesso non consentito.{" "}
					<Button onClick={() => refresh().catch(() => {})}>Riprova</Button>
				</Alert>
			)}
			{!pipelineError && !loadingPlayers && !canWrite && (
				<Alert severity="info">Pipeline condivisa in sola lettura.</Alert>
			)}

			<div className="pipe-page">
				{/* Nel PDF l'intestazione della pagina non c'è */}
				<div className="pipe-header">
					<div className="pipe-header__left">
						<div className="pipe-header__title">Obiettivi di mercato</div>
						<div className="pipe-header__sub">
							{totalPlayers} giocator{totalPlayers === 1 ? "e" : "i"} seguit
							{totalPlayers === 1 ? "o" : "i"}
						</div>
					</div>
					<div className="pipe-header__actions">
						<ChoiceChips
							options={[
								{value: "stages", label: "Per stadio"},
								{value: "departments", label: "Da monitorare"},
							]}
							value={view}
							onChange={setView}
						/>
						{view === "departments" && (
							<div className="pipe-sort-select">
								<VLFSelectNew
									aria-label="Ordina"
									options={SORT_OPTIONS}
									value={SORT_OPTIONS.find((o) => o.value === departmentSort)}
									onChange={(option) =>
										option && setDepartmentSort(option.value)
									}
									sorted={false}
									isClearable={false}
									noMargin
								/>
							</div>
						)}
					</div>
				</div>

				{view === "departments" ? (
					<DepartmentBoard
						canWrite={canWrite}
						pipeline={pipeline}
						players={players}
						ratings={ratings}
						visions={visions}
						reports={reports}
						sort={departmentSort}
						onAdd={setAddModal}
						onMove={handleMove}
						onRemove={handleRemove}
						onNoteChange={handleNoteChange}
					/>
				) : (
					<div className="pipe-board">
						{STAGES.map((stage, i) => {
							const entries = getEntriesForStage(stage.id);
							return (
								<PipelineColumn
									canWrite={canWrite}
									key={stage.id}
									stage={stage}
									stageIndex={i}
									entries={entries}
									players={players}
									ratings={ratings}
									onAdd={setAddModal}
									onMove={handleMove}
									onRemove={handleRemove}
									onNoteChange={handleNoteChange}
									dragRef={dragRef}
									dragOverTarget={dragOverTarget}
									setDragOverTarget={setDragOverTarget}
									handleDrop={handleDrop}
									handleDragEnd={handleDragEnd}
								/>
							);
						})}
					</div>
				)}
			</div>

			{/* Add player modal */}
			<AddPlayerModal
				open={!!addModal}
				stageName={addModalStage?.label || ""}
				onClose={() => setAddModal(null)}
				onAdd={handleAdd}
				alreadyInPipeline={alreadyInPipeline}
				handleNotification={handleNotification}
			/>
		</Page>
	);
}
